import { useEffect } from "react";

export const GOOGLE_TAG_ID = process.env.REACT_APP_GOOGLE_TAG_ID || "";

// Create these conversion actions in Google Ads (Goals > Conversions > New)
// and paste the full label (AW-11484315428/xxxxx) for each:
export const GOOGLE_CONVERSION_LABEL_SIGNUP = GOOGLE_TAG_ID
  ? `${GOOGLE_TAG_ID}/HsVrCIrngqgcEKT-kuQq`
  : "";
export const GOOGLE_CONVERSION_LABEL_DIGITAL_SIGNUP = GOOGLE_TAG_ID
  ? `${GOOGLE_TAG_ID}/npFUCKbfgqgcEKT-kuQq`
  : "";
export const GOOGLE_CONVERSION_LABEL_PAYMENT = GOOGLE_TAG_ID
  ? `${GOOGLE_TAG_ID}/aQ5WCOOQmagcEKT-kuQq`
  : "";

export const getSignupConversionLabel = (completionStateName?: string) => {
  if (completionStateName === "get-started-digital-subscription") {
    return GOOGLE_CONVERSION_LABEL_DIGITAL_SIGNUP;
  }
  return GOOGLE_CONVERSION_LABEL_SIGNUP;
};

export const googleEventNameAccountCreation = (
  completionStateName?: string,
) => {
  if (completionStateName === "get-started-digital-subscription") {
    return "account-creation-complete-digital-subscription";
  }
  return "account-creation-complete";
};

export const googleEventNamePayment = () => "payment-complete";

export const googleEventNamePageLoad = (flow: string) => `page-load-${flow}`;

type ReturnType = {
  fireConversionEvent: (eventId: string, eventName?: string) => void;
  firePageLoadEvent: (eventName: string) => void;
};

export default (conversionId?: string): ReturnType => {
  useEffect(() => {
    if (!conversionId) {
      console.warn("No conversion ID provided to useGoogleTracking");
      return;
    }
    let script: HTMLScriptElement | null = null;
    if (!document.querySelector(`script[src*="${conversionId}"]`)) {
      // Load the Google Tag Manager tracking script dynamically
      script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${conversionId}`;
      document.head.appendChild(script);
    }

    // Setup Google Tag Manager setup script dynamically
    const setupScript = document.createElement("script");
    setupScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${conversionId}');
    `;
    document.head.appendChild(setupScript);

    // Cleanup on component unmount
    return () => {
      if (script) {
        document.head.removeChild(script);
      }
      document.head.removeChild(setupScript);
    };
  }, [conversionId]);

  const fireConversionEvent = (eventId: string, eventName?: string) => {
    if (!eventId) {
      console.warn("No event ID provided to fireConversionEvent");
      return;
    }
    const windowWithGtag = window as Window & {
      gtag?: (
        command: string,
        action: string,
        params: { send_to: string; event_name?: string },
      ) => void;
    };

    if (typeof windowWithGtag.gtag === "function") {
      const params: { send_to: string; event_name?: string } = {
        send_to: eventId,
      };
      if (eventName) {
        params.event_name = eventName;
      }
      windowWithGtag.gtag("event", "conversion", params);
    } else {
      console.error(
        "window.gtag function is not defined. Google Analytics conversion event could not be sent.",
      );
    }
  };
  const firePageLoadEvent = (eventName: string) => {
    const windowWithGtag = window as Window & {
      gtag?: (
        command: string,
        action: string,
        params: { event_name: string },
      ) => void;
    };

    if (typeof windowWithGtag.gtag === "function") {
      windowWithGtag.gtag("event", "page_view", { event_name: eventName });
    }
  };

  return { fireConversionEvent, firePageLoadEvent };
};
